import type {
  AdminPaymentDTO,
  PaymentDocumentDTO,
  PaymentReceiptInput,
} from "@ai-crm/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Pagination } from "../../components/pagination";
import {
  adminApi,
  ApiError,
  serializePaymentReceiptMetadata,
} from "../../lib/api";
import { usePaymentDocuments } from "../../lib/payment-documents";
import {
  paymentButton,
  paymentKeys,
  usePaymentScope,
  usePaymentWrite,
  useVisiblePage,
} from "../../lib/payment-ui";

export function PaymentReceipts({
  accountId,
  payment,
  onChanged,
}: {
  accountId: string;
  payment: AdminPaymentDTO;
  onChanged: () => Promise<void>;
}) {
  const scope = usePaymentScope(accountId);
  const { capture } = scope;
  const write = usePaymentWrite(accountId);
  const docs = usePaymentDocuments(accountId);
  const visible = useVisiblePage();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack.at(-1);
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<PaymentDocumentDTO | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const acknowledged = useRef<string | null>(null);
  const paginationLocked = write.locked || processing?.state === "WRITING";
  const history = useQuery({
    queryKey: [
      ...paymentKeys.account(scope.adminId, accountId),
      "receipts",
      payment.id,
      cursor ?? "",
    ],
    queryFn: ({ signal }) =>
      scope.read(signal, () =>
        adminApi.paymentReceipts(
          scope.token!,
          accountId,
          payment.id,
          { cursor, limit: 10 },
          signal,
        ),
      ),
    enabled: Boolean(scope.token),
    refetchInterval: (q) =>
      visible &&
      (processing?.state === "WRITING" ||
        q.state.data?.items.some((d) => d.state === "WRITING"))
        ? 3000
        : false,
  });
  useEffect(() => {
    const tracked = history.data?.items.find((d) => d.id === processing?.id);
    const changed =
      tracked && tracked.state !== "WRITING" ? tracked : undefined;
    if (!changed || acknowledged.current === changed.id) return;
    acknowledged.current = changed.id;
    setProcessing(changed);
    if (changed.state === "READY") {
      setMessage("Чек прикреплён.");
      setFile(null);
      setReason("");
    } else
      setError(
        `Новый чек не прикреплён (${changed.errorCode ?? "ошибка обработки"}). Прежний чек сохранён. После сверки загрузите файл заново.`,
      );
    void onChanged();
  }, [history.data, processing?.id, onChanged]);
  // Processing is durable on the server; refreshing metadata is a read, not cancellation.
  useEffect(() => {
    if (
      !visible ||
      !(
        processing?.state === "WRITING" ||
        history.data?.items.some((d) => d.state === "WRITING")
      )
    )
      return;
    const guard = capture();
    const timer = window.setInterval(() => {
      if (guard.isCurrent()) void onChanged();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [visible, processing?.state, history.data, capture, onChanged]);
  async function upload() {
    if (!scope.token || write.locked || processing?.state === "WRITING") return;
    if (
      !file ||
      file.size > 10 * 1024 * 1024 ||
      file.size === 0 ||
      (file.type !== "application/pdf" &&
        !file.name.toLowerCase().endsWith(".pdf"))
    ) {
      setError("Выберите один PDF размером до 10 МиБ.");
      return;
    }
    if (payment.currentReceipt && !reason.trim()) {
      setError("Укажите причину замены чека.");
      return;
    }
    const guard = capture();
    const token = scope.token,
      fixedFile = file;
    const input: PaymentReceiptInput = {
      requestId: crypto.randomUUID(),
      expectedVersion: payment.version,
      reason: reason.trim() || null,
    };
    try {
      serializePaymentReceiptMetadata(input);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Проверьте причину замены чека.",
      );
      return;
    }
    setError(null);
    setMessage(null);
    let result: PaymentDocumentDTO | null = null;
    await write.run(
      input,
      async (body) => {
        const accepted = await adminApi.uploadPaymentReceipt(
          token,
          accountId,
          payment.id,
          body,
          fixedFile,
        );
        result = accepted.document;
      },
      async () => {
        if (result) {
          setProcessing(result);
          acknowledged.current = null;
          setCursorStack([]);
          if (result.state === "READY") {
            setMessage("Чек прикреплён.");
            setFile(null);
            setReason("");
          }
        }
        await history.refetch();
        if (guard.isCurrent()) await onChanged();
      },
      async (caught) => {
        if (caught instanceof ApiError && caught.status === 409) {
          await onChanged();
          if (guard.isCurrent()) await history.refetch();
        }
      },
    );
  }
  function download(documentId: string) {
    if (scope.token)
      void docs.fetchDocument(
        () =>
          adminApi.paymentReceiptPdf(
            scope.token!,
            accountId,
            payment.id,
            documentId,
          ),
        `receipt-${documentId}.pdf`,
      );
  }
  return (
    <section
      className="space-y-3 rounded-md border p-3"
      aria-label={`Чеки перевода ${payment.id}`}
    >
      <h4 className="font-medium">Чек</h4>
      {payment.currentReceipt?.state === "READY" ? (
        <button
          className={paymentButton}
          disabled={docs.busy}
          onClick={() => download(payment.currentReceipt!.id)}
        >
          Скачать актуальный чек
        </button>
      ) : (
        <p className="text-sm">Чек ещё не прикреплён</p>
      )}
      <p className="text-sm text-muted-foreground">
        Чек сформирован в «Мой налог»; файл станет доступен клиенту в его
        платежах.
      </p>
      <label className="block space-y-1 text-sm">
        <span>PDF чека, до 10 МиБ</span>
        <input
          className="control"
          type="file"
          accept="application/pdf"
          disabled={write.locked || processing?.state === "WRITING"}
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setError(null);
          }}
        />
      </label>
      <label className="block space-y-1 text-sm">
        <span>Причина замены чека</span>
        <textarea
          className="control"
          value={reason}
          maxLength={4000}
          disabled={write.locked || processing?.state === "WRITING"}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {!write.uncertain && (
        <button
          className={paymentButton}
          disabled={!file || write.locked || processing?.state === "WRITING"}
          onClick={() => void upload()}
        >
          Прикрепить чек
        </button>
      )}
      {write.uncertain && (
        <button
          className={paymentButton}
          disabled={write.pending}
          onClick={() => void write.retry()}
        >
          Повторить загрузку того же чека
        </button>
      )}
      {processing?.state === "WRITING" && (
        <p role="status">Новый чек обрабатывается</p>
      )}
      {message && <p role="status">{message}</p>}
      {(error || write.error || docs.error) && (
        <p role="alert" className="text-sm text-danger">
          {error ?? write.error ?? docs.error}
        </p>
      )}
      <details>
        <summary className="cursor-pointer text-sm font-medium">
          История версий чеков
        </summary>
        {history.isPending && <p role="status">Загружаем версии…</p>}
        {history.isError && (
          <p role="alert">
            Не удалось загрузить версии.{" "}
            <button
              className={paymentButton}
              onClick={() => void history.refetch()}
            >
              Повторить
            </button>
          </p>
        )}
        {history.data?.items.map((d) => (
          <div
            key={d.id}
            className="my-2 flex flex-wrap items-center gap-2 text-sm"
          >
            <span>
              Версия {d.version} ·{" "}
              {d.state === "READY"
                ? "Готов"
                : d.state === "WRITING"
                  ? "Обрабатывается"
                  : `Ошибка: ${d.errorCode ?? "неизвестна"}`}
            </span>
            {d.state === "READY" && (
              <button
                className={paymentButton}
                disabled={docs.busy}
                onClick={() => download(d.id)}
              >
                Скачать версию {d.version}
              </button>
            )}
          </div>
        ))}
        <Pagination
          canGoBack={!paginationLocked && cursorStack.length > 0}
          canGoForward={Boolean(
            !paginationLocked &&
            history.data?.nextCursor &&
            !cursorStack.includes(history.data.nextCursor),
          )}
          onBack={() => {
            if (!paginationLocked) setCursorStack((s) => s.slice(0, -1));
          }}
          onForward={() => {
            if (paginationLocked) return;
            const next = history.data?.nextCursor;
            if (next && !cursorStack.includes(next))
              setCursorStack((s) => [...s, next]);
          }}
          isLoading={history.isFetching}
        />
      </details>
    </section>
  );
}
